// ---------------------------------------------------------------------------
// YouAreYou (uru) — Trust Link Settings Modal V2
//
// Redesigned to match ContactDetailModal's visual language:
//   - Same accordion bars, field row style, and pill-mode toggles
//   - Profile cards (You + Them) with live factor previews below name
//   - "What I Share" / "What I Ask" tab bar (mobile) / column labels (desktop)
//   - BASIC and ENHANCED collapsible sections
//
// Used by DashboardPage.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from "react";
import Icon from "@/components/ui/Icon";
import { useAuth } from "@/hooks/useAuth";
import { resolveAvatarUrl } from "@/types/api";
import { getInitials } from "@/lib/utils";
import FactorSettingsPanel from "@/components/ui/FactorSettingsPanel";
import type { FactorDef } from "@/components/ui/FactorSettingsPanel";
import {
  buildDefaultFactors,
  createDefaultSettingsV2,
  BASIC_FACTORS,
  ENHANCED_FACTORS,
} from "@/components/ui/FactorSettingsPanel";

// ---------------------------------------------------------------------------
// Re-export types so DashboardPage imports continue to work unchanged
// ---------------------------------------------------------------------------

export type { DataShareMode, FactorSetting, TrustLinkSettingsV2, MyFactorData } from "@/components/ui/FactorSettingsPanel";
export { buildDefaultFactors };

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface TrustLinkSettingsModalV2Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (settings: import("@/components/ui/FactorSettingsPanel").TrustLinkSettingsV2) => void;
  onConfirm?: (settings: import("@/components/ui/FactorSettingsPanel").TrustLinkSettingsV2) => void;
  initialSettings?: Partial<import("@/components/ui/FactorSettingsPanel").TrustLinkSettingsV2>;
  loading?: boolean;
  /** @deprecated — now pulled from useAuth inside the modal */
  myName?: string;
  /** @deprecated — now pulled from useAuth inside the modal */
  myAvatarUrl?: string;
  /** Passed through to FactorSettingsPanel for KYC gate checks */
  myData?: import("@/components/ui/FactorSettingsPanel").MyFactorData;
}

// ---------------------------------------------------------------------------
// Modal entry point
// ---------------------------------------------------------------------------

export default function TrustLinkSettingsModalV2({
  isOpen, ...props
}: TrustLinkSettingsModalV2Props) {
  if (!isOpen) return null;
  return <Inner {...props} />;
}

// ---------------------------------------------------------------------------
// Inner modal — mounts fresh each open
// ---------------------------------------------------------------------------

function Inner({
  onClose, onSave, onConfirm, initialSettings, loading, myData,
}: Omit<TrustLinkSettingsModalV2Props, "isOpen">) {
  const { user } = useAuth();

  const myName     = user ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() || "You" : "You";
  const myInitials = user ? getInitials(user.first_name ?? "", user.last_name ?? "") : "?";
  const myAvatar   = user ? resolveAvatarUrl(user.avatar) : null;
  const myPhone    = user?.phone_number || undefined;
  const myEmail    = user?.email && !user.email.endsWith("@uru.youareyou.io") ? user.email : undefined;

  const [settings, setSettings] = useState(() => createDefaultSettingsV2(initialSettings));

  const modalRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Scroll lock
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Focus close button on mount
  useEffect(() => {
    const t = setTimeout(() => closeRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  // Escape + focus trap
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") { e.preventDefault(); handleClose(); return; }
      if (e.key !== "Tab" || !modalRef.current) return;
      const els = modalRef.current.querySelectorAll<HTMLElement>(
        'button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])',
      );
      if (!els.length) return;
      const first = els[0], last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const handleChange = useCallback(
    (key: string, side: "my" | "their", mode: import("@/components/ui/FactorSettingsPanel").DataShareMode) => {
      if (side === "my" && myData?.kyc && myData.kyc[key] === false) return;
      setSettings((prev) => ({
        ...prev,
        factors: prev.factors.map((f) =>
          f.key === key ? { ...f, [side === "my" ? "myMode" : "theirMode"]: mode } : f,
        ),
      }));
    },
    [myData],
  );

  const handleBulkChange = useCallback(
    (defs: FactorDef[], side: "my" | "their", mode: import("@/components/ui/FactorSettingsPanel").DataShareMode) => {
      const keys = new Set(defs.map((d) => d.key));
      setSettings((prev) => ({
        ...prev,
        factors: prev.factors.map((f) => {
          if (!keys.has(f.key)) return f;
          if (side === "my" && myData?.kyc && myData.kyc[f.key] === false) return f;
          const def = [...BASIC_FACTORS, ...ENHANCED_FACTORS].find((d) => d.key === f.key);
          const effectiveMode = mode === "share" && def?.noShare ? "valid" : mode;
          return { ...f, [side === "my" ? "myMode" : "theirMode"]: effectiveMode };
        }),
      }));
    },
    [myData],
  );

  const handleClose   = useCallback(() => { onSave(settings); onClose(); }, [settings, onSave, onClose]);
  const handleConfirm = useCallback(() => { onSave(settings); onConfirm?.(settings); onClose(); }, [settings, onSave, onConfirm, onClose]);
  const handleBackdrop = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleClose();
  }, [handleClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center md:items-center"
      onClick={handleBackdrop}
      role="presentation"
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />

      <div
        ref={modalRef}
        role="dialog" aria-modal="true" aria-labelledby="tls-v2-title"
        className="relative z-10 flex max-h-[100dvh] w-full flex-col bg-white shadow-2xl h-full max-w-[430px] md:h-auto md:max-h-[90vh] md:max-w-2xl md:rounded-2xl overflow-hidden"
      >
        {/* ── Header bar — matches ContactDetailModal header exactly ── */}
        <div className="flex shrink-0 items-center border-b border-gray-200 bg-white px-4 py-3">
          <button
            ref={closeRef}
            onClick={handleClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted hover:bg-gray-100 hover:text-text-primary focus:outline-none"
            aria-label="Close"
          >
            <Icon name="close" className="text-xl" />
          </button>
          <h2
            id="tls-v2-title"
            className="flex-1 pr-8 text-center text-xs font-bold uppercase tracking-widest text-text-secondary"
          >
            Trust Link Settings
          </h2>
        </div>

        {/* ── Scrollable body — FactorSettingsPanel ── */}
        <div className="flex-1 overflow-y-auto">
          <FactorSettingsPanel
            settings={settings}
            onChange={handleChange}
            onBulkChange={handleBulkChange}
            myName={myName}
            myInitials={myInitials}
            myAvatarUrl={myAvatar}
            myData={{
              phone: myPhone,
              email: myEmail,
              kyc:   myData?.kyc,
            }}
          />
        </div>

        {/* ── Footer ── */}
        <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-3 space-y-2">
          <button
            type="button"
            onClick={onConfirm ? handleConfirm : handleClose}
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary-500 py-3 text-sm font-bold text-white shadow-lg shadow-primary-500/20 transition-all hover:bg-primary-600 active:scale-[0.98] disabled:opacity-60"
          >
            {loading ? (
              <><div className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />Saving…</>
            ) : onConfirm ? (
              <><Icon name="add_link" className="text-base" />Create Link</>
            ) : "Save"}
          </button>
          {onConfirm && (
            <button type="button" onClick={handleClose}
              className="w-full py-2 text-sm font-semibold text-text-muted hover:text-text-primary transition-colors focus:outline-none">
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
